/**
 * ModelFormRows.js
 * Composants de formulaire : SeriesRow, ParamRow, OutputRow
 */
import React, {useState} from "react";
import {useTranslation} from "react-i18next";
import {
    Accordion, AccordionDetails, AccordionSummary,
    Chip, IconButton, MenuList, MenuItem, Paper, Popover, Stack, TextField, Tooltip, Typography
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";

const TYPES = [
    {
        category: "Numérique",
        values: [
            {id: "NUMERIC",  tooltip: "Valeur numérique générique (entier ou décimal)"},
            {id: "INTEGER",  tooltip: "Nombre entier (ex : 1, 42, -7)"},
            {id: "FLOAT",    tooltip: "Nombre décimal flottant (ex : 3.14, -0.5)"},
        ]
    },
    {
        category: "Texte",
        values: [
            {id: "STRING", tooltip: "Chaîne de caractères libre"},
        ]
    },
    {
        category: "Logique",
        values: [
            {id: "BOOLEAN", tooltip: "Valeur booléenne (true / false)"},
            {id: "BINARY",  tooltip: "Valeur binaire (0 / 1)"},
        ]
    },
    {
        category: "Catégoriel (multi valeur)",
        values: [
            {id: "CATEGORICAL", tooltip: "Ensemble de catégories discrètes (ex : rouge, vert, bleu)"},
            {id: "SELECT",      tooltip: "Sélection parmi une liste d'options prédéfinies"},
        ]
    },
    {
        category: "Série temporelle",
        values: [
            {id: "TIMESERIES",         tooltip: "Série de valeurs numériques indexées par le temps"},
            {id: "PROMISE_TIMESERIES", tooltip: "Série temporelle au format Promise (avec métadonnées de capteur)"},
            {id: "TIMESTAMP",          tooltip: "Horodatage unique (date + heure, ex : 2024-01-15T10:30:00Z)"},
            {id: "INTERVAL",           tooltip: "Intervalle de temps entre deux instants (ex : durée en ms)"},
        ]
    },
    {
        category: "Liste / Collection",
        values: [
            {id: "ARRAY_NUMERIC",    tooltip: "Liste ordonnée de valeurs numériques (ex : [1.2, 3.4, 5.6])"},
            {id: "ARRAY_INTEGER",    tooltip: "Liste ordonnée d'entiers (ex : [1, 2, 3])"},
            {id: "ARRAY_FLOAT",      tooltip: "Liste ordonnée de décimaux (ex : [1.1, 2.2])"},
            {id: "ARRAY_STRING",     tooltip: "Liste ordonnée de chaînes de caractères"},
            {id: "ARRAY_BOOLEAN",    tooltip: "Liste ordonnée de booléens (ex : [true, false, true])"},
            {id: "ARRAY_TIMESERIES", tooltip: "Liste de séries temporelles (ex : multi-capteurs)"},
            {id: "ARRAY_CATEGORICAL",tooltip: "Liste de valeurs catégorielles"},
            {id: "MATRIX",           tooltip: "Tableau 2D de valeurs numériques (matrice m×n)"},
        ]
    },
];

function TypeSelectPopover({value, onChange, disabled, label, sx}) {
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);
    const handleOpen = (e) => { if (!disabled) setAnchorEl(e.currentTarget); };
    const handleClose = () => setAnchorEl(null);
    const handleSelect = (id) => { onChange(id); handleClose(); };
    const currentCategory = TYPES.find(g => g.values.some(tp => tp.id === value))?.category ?? null;

    return (
        <>
            <TextField
                label={label} size="small" value={value ?? ""} disabled={disabled}
                onClick={handleOpen} sx={{...sx, cursor: disabled ? "default" : "pointer"}}
                slotProps={{input: {readOnly: true, endAdornment: <ArrowDropDownIcon sx={{fontSize: 20, color: "action.active", mr: -0.5}}/>}}}
            />
            <Popover open={open} anchorEl={anchorEl} onClose={handleClose}
                     anchorOrigin={{vertical: "bottom", horizontal: "left"}}
                     transformOrigin={{vertical: "top", horizontal: "left"}}
                     slotProps={{paper: {sx: {width: 300, maxHeight: 420, overflow: "auto"}}}}>
                {TYPES.map(group => (
                    <Accordion key={group.category} defaultExpanded={group.category === currentCategory}
                               disableGutters elevation={0} sx={{"&:before": {display: "none"}}}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon/>}
                                          sx={{bgcolor: "action.hover", minHeight: 34, "& .MuiAccordionSummary-content": {my: 0.5}}}>
                            <Typography variant="caption" fontWeight="bold">{group.category}</Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={{p: 0}}>
                            <MenuList dense disablePadding>
                                {group.values.map(tp => (
                                    <Tooltip key={tp.id} title={tp.tooltip} placement="right" arrow>
                                        <MenuItem selected={tp.id === value}
                                                  onClick={() => handleSelect(tp.id)}
                                                  sx={{fontSize: 13, pl: 2}}>
                                            {tp.id}
                                        </MenuItem>
                                    </Tooltip>
                                ))}
                            </MenuList>
                        </AccordionDetails>
                    </Accordion>
                ))}
            </Popover>
        </>
    );
}

export function SeriesRow({series, index, onRemove, onUpdate, canDelete = true, allNames = [], disabled = false}) {
    const {t} = useTranslation();
    const isDuplicate = !disabled && series.name && allNames.filter(n => n === series.name).length > 1;
    return (
        <Paper variant="outlined" sx={{p: 1.5, borderRadius: 1, bgcolor: "action.hover"}}>
            <Stack
                direction="row"
                sx={{
                    alignItems: "center",
                    mb: 0.75
                }}>
                <Chip label={`S${index + 1}`} size="small" color="primary" variant="outlined" sx={{fontSize: 11, mr: 1}}/>
                <div style={{flex: 1}}/>
                {canDelete && !disabled && <IconButton size="small" color="error" onClick={onRemove}><DeleteIcon sx={{fontSize: 16}}/></IconButton>}
            </Stack>
            <Stack spacing={1}>
                <Stack direction="row" spacing={1}>
                    <Tooltip title={t("admin.catalog.editor.series.tooltipName")} placement="top" arrow>
                        <TextField label="name" size="small" sx={{flex: 1}} disabled={disabled} error={isDuplicate}
                                   helperText={isDuplicate ? t("admin.catalog.editor.duplicateName") : ""}
                                   value={series.name ?? ""} onChange={(e) => onUpdate("name", e.target.value)}/>
                    </Tooltip>
                    <Tooltip title={t("admin.catalog.editor.series.tooltipLabel")} placement="top" arrow>
                        <TextField label="label" size="small" sx={{flex: 1}} disabled={disabled}
                                   value={series.label ?? ""} onChange={(e) => onUpdate("label", e.target.value)}/>
                    </Tooltip>
                    <Tooltip title={t("admin.catalog.editor.series.tooltipColumnName")} placement="top" arrow>
                        <TextField label="columnName" size="small" sx={{flex: 0.8}} disabled={disabled}
                                   value={series.columnName ?? "value"} onChange={(e) => onUpdate("columnName", e.target.value)}/>
                    </Tooltip>
                    <Tooltip title={t("admin.catalog.editor.series.tooltipDataType")} placement="top" arrow>
                        <TypeSelectPopover label="dataType" sx={{flex: 0.8}} disabled={disabled}
                                           value={series.dataType ?? "NUMERIC"} onChange={(v) => onUpdate("dataType", v)}/>
                    </Tooltip>
                </Stack>
                <Tooltip title={t("admin.catalog.editor.series.tooltipDescription")} placement="top" arrow>
                    <TextField label="description" size="small" fullWidth disabled={disabled}
                               value={series.description ?? ""} onChange={(e) => onUpdate("description", e.target.value)}/>
                </Tooltip>
            </Stack>
        </Paper>
    );
}

export function ParamRow({param, index, onRemove, onUpdate, allNames = [], disabled = false}) {
    const {t} = useTranslation();
    const isDuplicate = !disabled && param.name && allNames.filter(n => n === param.name).length > 1;
    return (
        <Paper variant="outlined" sx={{p: 1.5, borderRadius: 1, bgcolor: "action.hover"}}>
            <Stack
                direction="row"
                sx={{
                    alignItems: "center",
                    mb: 0.75
                }}>
                <Chip label={`P${index + 1}`} size="small" color="secondary" variant="outlined" sx={{fontSize: 11, mr: 1}}/>
                <div style={{flex: 1}}/>
                {!disabled && <IconButton size="small" color="error" onClick={onRemove}><DeleteIcon sx={{fontSize: 16}}/></IconButton>}
            </Stack>
            <Stack spacing={1}>
                <Stack direction="row" spacing={1}>
                    <Tooltip title={t("admin.catalog.editor.param.tooltipName")} placement="top" arrow>
                        <TextField label="name" size="small" sx={{flex: 1}} disabled={disabled} error={isDuplicate}
                                   helperText={isDuplicate ? t("admin.catalog.editor.duplicateName") : ""}
                                   value={param.name ?? ""} onChange={(e) => onUpdate("name", e.target.value)}/>
                    </Tooltip>
                    <Tooltip title={t("admin.catalog.editor.param.tooltipLabel")} placement="top" arrow>
                        <TextField label="label" size="small" sx={{flex: 1}} disabled={disabled}
                                   value={param.label ?? ""} onChange={(e) => onUpdate("label", e.target.value)}/>
                    </Tooltip>
                    <Tooltip title={t("admin.catalog.editor.param.tooltipType")} placement="top" arrow>
                        <TypeSelectPopover label="type" sx={{flex: 0.7}} disabled={disabled}
                                           value={param.type ?? "FLOAT"} onChange={(v) => onUpdate("type", v)}/>
                    </Tooltip>
                    <Tooltip title={t("admin.catalog.editor.param.tooltipDefault")} placement="top" arrow>
                        <TextField label="default" size="small" sx={{flex: 0.7}} disabled={disabled}
                                   value={param.default ?? ""}
                                   onChange={(e) => {
                                       const v = e.target.value;
                                       onUpdate("default", v === "" ? null : isNaN(Number(v)) ? v : Number(v));
                                   }}/>
                    </Tooltip>
                </Stack>
                <Stack direction="row" spacing={1}>
                    <Tooltip title={t("admin.catalog.editor.param.tooltipMin")} placement="top" arrow>
                        <TextField label="min" type="number" size="small" sx={{flex: 1}} disabled={disabled}
                                   value={param.min ?? ""} onChange={(e) => onUpdate("min", e.target.value === "" ? null : parseFloat(e.target.value))}/>
                    </Tooltip>
                    <Tooltip title={t("admin.catalog.editor.param.tooltipMax")} placement="top" arrow>
                        <TextField label="max" type="number" size="small" sx={{flex: 1}} disabled={disabled}
                                   value={param.max ?? ""} onChange={(e) => onUpdate("max", e.target.value === "" ? null : parseFloat(e.target.value))}/>
                    </Tooltip>
                    <Tooltip title={t("admin.catalog.editor.param.tooltipOptions")} placement="top" arrow>
                        <TextField label={t("admin.catalog.editor.param.labelOptions")} size="small" sx={{flex: 2}} disabled={disabled}
                                   value={(param.options ?? []).join(", ")}
                                   onChange={(e) => onUpdate("options", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))}
                                   helperText={param.type === "SELECT" ? t("admin.catalog.editor.param.helperSelectRequired") : ""}/>
                    </Tooltip>
                </Stack>
                <Tooltip title={t("admin.catalog.editor.param.tooltipDescription")} placement="top" arrow>
                    <TextField label="description" size="small" fullWidth disabled={disabled}
                               value={param.description ?? ""} onChange={(e) => onUpdate("description", e.target.value)}/>
                </Tooltip>
            </Stack>
        </Paper>
    );
}

export function OutputRow({output, index, onRemove, onUpdate, allNames = [], disabled = false}) {
    const {t} = useTranslation();
    const isDuplicate = !disabled && output.name && allNames.filter(n => n === output.name).length > 1;
    return (
        <Paper variant="outlined" sx={{p: 1.5, borderRadius: 1, bgcolor: "action.hover"}}>
            <Stack
                direction="row"
                sx={{
                    alignItems: "center",
                    mb: 0.75
                }}>
                <Chip label={`O${index + 1}`} size="small" color="info" variant="outlined" sx={{fontSize: 11, mr: 1}}/>
                <div style={{flex: 1}}/>
                {!disabled && <IconButton size="small" color="error" onClick={onRemove}><DeleteIcon sx={{fontSize: 16}}/></IconButton>}
            </Stack>
            <Stack spacing={1}>
                <Stack direction="row" spacing={1}>
                    <Tooltip title={t("admin.catalog.editor.output.tooltipName")} placement="top" arrow>
                        <TextField label="name" size="small" sx={{flex: 1}} disabled={disabled} error={isDuplicate}
                                   helperText={isDuplicate ? t("admin.catalog.editor.duplicateName") : ""}
                                   value={output.name ?? ""} onChange={(e) => onUpdate("name", e.target.value)}/>
                    </Tooltip>
                    <Tooltip title={t("admin.catalog.editor.output.tooltipLabel")} placement="top" arrow>
                        <TextField label="label" size="small" sx={{flex: 1}} disabled={disabled}
                                   value={output.label ?? ""} onChange={(e) => onUpdate("label", e.target.value)}/>
                    </Tooltip>
                    <Tooltip title={t("admin.catalog.editor.output.tooltipDataType")} placement="top" arrow>
                        <TypeSelectPopover label="dataType" sx={{flex: 0.8}} disabled={disabled}
                                           value={output.dataType ?? "NUMERIC"} onChange={(v) => onUpdate("dataType", v)}/>
                    </Tooltip>
                    <Tooltip title={t("admin.catalog.editor.output.tooltipUnit")} placement="top" arrow>
                        <TextField label="unit" size="small" sx={{flex: 0.6}} disabled={disabled} placeholder="ratio, ms, …"
                                   value={output.unit ?? ""} onChange={(e) => onUpdate("unit", e.target.value || null)}/>
                    </Tooltip>
                </Stack>
                <Tooltip title={t("admin.catalog.editor.output.tooltipDescription")} placement="top" arrow>
                    <TextField label="description" size="small" fullWidth disabled={disabled}
                               value={output.description ?? ""} onChange={(e) => onUpdate("description", e.target.value)}/>
                </Tooltip>
            </Stack>
        </Paper>
    );
}
